// SecurityPanel — admin Security Dashboard (Audit log / Sessions / IP management),
// extracted from src/App.jsx (C1 refactor, behavior-preserving). Moved verbatim; only
// imports adjusted. Rendered directly by the page router (still in App.jsx) for the
// "security" page. Original block-comment (endpoint contract) preserved below.
import { useState, useEffect } from "react";
import { ChevronLeft } from 'lucide-react';
import { I } from '../icons.jsx';
import { VERSION } from '../version.js';
import * as tz from '../lib/tz.js';   // DST-aware business time (Europe/Kyiv)
import { useToast } from '../contexts/toast-context.jsx';
import { getSessionToken } from '../utils/session.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';

// ── SecurityPanel (2026-04-17): admin UI для Audit log / Sessions / IP management ──
// Три таба в одном разделе: forensics + kill sessions + ban/unban IPs без лезть на сервер.
// Использует backend endpoints добавленные в commit 4a1c1805:
//   GET  /api/audit                    — audit events за дату
//   GET  /api/admin/audit/dates        — список дат доступных audit files
//   GET  /api/admin/sessions           — активные сессии
//   DELETE /api/admin/sessions/:token  — kill session (force logout)
//   GET  /api/admin/banned             — blacklist IPs
//   POST /api/admin/ban                — manual ban (с validation: no localhost/private)
//   POST /api/admin/unban              — unblock (fix: теперь обновляет PERM_BLOCKED_IPS)
//   GET  /api/admin/login-attempts     — in-memory counters (кто пытается брут-форсить)
//   DELETE /api/admin/login-attempts/:ip — reset counter
//
// ── Finance audit tab (2026-06-09, PR6 of unified finance-audit) ──
// Read-only viewer over GET /api/admin/finance-audit (requireAdmin). Shows all
// finance-table mutations (atomic approve/paid, field-edits, bulk ops, deletes)
// with old→new diff + per-record change-history (click recordId) + CSV export.
// Backend already live in production (PRs 1-5). This is the frontend only.

// Classify + normalize a finance-audit diff for rendering. Pure — exported for tests.
// The backend `diff` field has three shapes (plus null):
//   - field/status-diff: { <fieldName>: { from:<old>, to:<new> }, … } (one or more fields)
//   - set-diff (bulk/delete): { modified:[ids], removed:[ids], added:[ids] } (any subset)
//   - null/unrecognized → render only `details`
// Returns one of:
//   { kind:'field', fields:[{key, from, to}] }
//   { kind:'set',   counts:{modified,removed,added}, ids:{modified:[],removed:[],added:[]} }
//   { kind:'none' }   (null/empty/unrecognized → render only details)
export function describeFinanceDiff(diff) {
  if (!diff || typeof diff !== 'object') return { kind: 'none' };
  const isSet = ['modified', 'removed', 'added'].some(k => Array.isArray(diff[k]));
  if (isSet) {
    const ids = {
      modified: Array.isArray(diff.modified) ? diff.modified : [],
      removed:  Array.isArray(diff.removed)  ? diff.removed  : [],
      added:    Array.isArray(diff.added)    ? diff.added    : [],
    };
    return { kind: 'set', counts: { modified: ids.modified.length, removed: ids.removed.length, added: ids.added.length }, ids };
  }
  const fields = Object.entries(diff)
    .filter(([, v]) => v && typeof v === 'object' && ('from' in v || 'to' in v))
    .map(([key, v]) => ({ key, from: v.from, to: v.to }));
  return fields.length ? { kind: 'field', fields } : { kind: 'none' };
}

// Render a single diff value as a display string. Pure — exported for tests.
// undefined (key absent in this side of the diff) → ∅; null → 'null'; object → JSON.
export function fmtFinanceDiffVal(v) {
  if (v === undefined) return '∅';
  if (v === null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function SecurityPanel({ onBack, user }) {
  const { addToast } = useToast();
  const [tab, setTab] = useState('audit');
  const token = localStorage.getItem('blitz_token') || getSessionToken();
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Подтверждения. Промис-обёртка вокруг ConfirmDialog: системный confirm() был
  // синхронным, и вызовы читались как `if (!confirm(...)) return;`. askConfirm
  // сохраняет ту же форму (`if (!(await askConfirm(...))) return;`), поэтому тела
  // действий ниже не пришлось разбирать на «спросить» и «сделать».
  const [confirmState, setConfirmState] = useState(null);
  const askConfirm = (opts) => new Promise((resolve) => setConfirmState({ opts, resolve }));
  const closeConfirm = (answer) => {
    confirmState?.resolve(answer);
    setConfirmState(null);
  };

  // Audit tab state
  const [auditDate, setAuditDate] = useState(tz.dateKey());   // Kyiv today (DST-aware)
  const [auditDates, setAuditDates] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ type: '', user: '', ip: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditTotal, setAuditTotal] = useState(0);
  const AUDIT_PAGE_SIZE = 50;

  // Sessions tab state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsSearch, setSessionsSearch] = useState('');
  // 2026-08-07: доверенные устройства — метки, пропускающие 2FA при входе на 30 дней.
  // Живут во вкладке Sessions, потому что вопрос один и тот же: «кто сейчас имеет доступ».
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // IPs tab state
  const [blacklist, setBlacklist] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [banForm, setBanForm] = useState({ ip: '', reason: '' });
  const [ipsLoading, setIpsLoading] = useState(false);

  // Finance audit tab state
  const _today = tz.dateKey();   // Kyiv today (DST-aware)
  const [financeFrom, setFinanceFrom] = useState(_today);
  const [financeTo, setFinanceTo] = useState(_today);
  const [financeEvents, setFinanceEvents] = useState([]);
  const [financeFilter, setFinanceFilter] = useState({ table: '', action: '', user: '', recordId: '', invoice: '', source: '' });
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeOffset, setFinanceOffset] = useState(0);
  const [financeTotal, setFinanceTotal] = useState(0);
  const [financeError, setFinanceError] = useState('');  // backend 400 (bad range) surfaced here
  const FINANCE_PAGE_SIZE = 50;
  const FINANCE_TABLES = ['payments', 'customer-payments', 'gen-balances', 'today-transactions', 'tx-archive', 'ds-archive', 'daily-summary', 'blitz-reports', 'blitz-reports-archive', 'selling-offers', 'selling-settings', 'daily-cap-overrides', 'invoices', 'invoice-settings', 'wallets', 'partners', 'daily-calcs-data', 'deals', 'crg-deals', 'offers', 'ftd-entries', 'monthly-targets', 'broker-book', 'counterparties'];

  const loadAuditDates = async () => {
    try {
      const r = await fetch('/api/admin/audit/dates', { headers: authHeaders });
      const d = await r.json();
      if (d.ok) setAuditDates(d.dates || []);
    } catch (e) { addToast('Failed to load audit dates: ' + e.message, 'error'); }
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({
        date: auditDate,
        limit: String(AUDIT_PAGE_SIZE),
        offset: String(auditOffset),
      });
      if (auditFilter.type) params.set('type', auditFilter.type);
      if (auditFilter.user) params.set('user', auditFilter.user);
      if (auditFilter.ip) params.set('ip', auditFilter.ip);
      const r = await fetch('/api/admin/audit?' + params.toString(), { headers: authHeaders });
      const d = await r.json();
      if (d.ok) {
        setAuditEvents(d.events || []);
        setAuditTotal(d.total || 0);
      } else {
        addToast('Failed: ' + (d.error || 'unknown'), 'error');
      }
    } catch (e) { addToast('Failed to load audit: ' + e.message, 'error'); }
    finally { setAuditLoading(false); }
  };

  // CSV export — downloads all filtered events (no pagination в CSV)
  const exportAuditCSV = async () => {
    try {
      const params = new URLSearchParams({ date: auditDate, format: 'csv' });
      if (auditFilter.type) params.set('type', auditFilter.type);
      if (auditFilter.user) params.set('user', auditFilter.user);
      if (auditFilter.ip) params.set('ip', auditFilter.ip);
      const r = await fetch('/api/admin/audit?' + params.toString(), { headers: authHeaders });
      if (!r.ok) { addToast('CSV export failed: ' + r.statusText, 'error'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${auditDate}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      addToast('✅ CSV downloaded', 'success');
    } catch (e) { addToast('❌ CSV export error: ' + e.message, 'error'); }
  };

  // ── Finance audit loaders ──
  // loadFinance accepts overrides so the recordId-history click works synchronously
  // (setState is async — we must not rely on the new filter/offset being committed yet).
  const loadFinance = async (opts = {}) => {
    const f = { ...financeFilter, ...(opts.filterOverride || {}) };
    const off = opts.offsetOverride != null ? opts.offsetOverride : financeOffset;
    setFinanceLoading(true);
    setFinanceError('');
    try {
      const params = new URLSearchParams({ from: financeFrom, to: financeTo, limit: String(FINANCE_PAGE_SIZE), offset: String(off) });
      if (f.table) params.set('table', f.table);
      if (f.action) params.set('action', f.action);
      if (f.user) params.set('user', f.user);
      if (f.recordId) params.set('recordId', f.recordId);
      if (f.invoice) params.set('invoice', f.invoice);
      if (f.source) params.set('source', f.source);
      const r = await fetch('/api/admin/finance-audit?' + params.toString(), { headers: authHeaders });
      const d = await r.json();
      if (d.ok) { setFinanceEvents(d.events || []); setFinanceTotal(d.total || 0); }
      else { setFinanceEvents([]); setFinanceTotal(0); setFinanceError(d.error || 'Unknown error'); }
    } catch (e) { setFinanceError(e.message); addToast('Failed to load finance audit: ' + e.message, 'error'); }
    finally { setFinanceLoading(false); }
  };

  // CSV export — downloads all filtered rows (no pagination в CSV)
  const exportFinanceCSV = async () => {
    try {
      const f = financeFilter;
      const params = new URLSearchParams({ from: financeFrom, to: financeTo, format: 'csv' });
      if (f.table) params.set('table', f.table);
      if (f.action) params.set('action', f.action);
      if (f.user) params.set('user', f.user);
      if (f.recordId) params.set('recordId', f.recordId);
      if (f.invoice) params.set('invoice', f.invoice);
      if (f.source) params.set('source', f.source);
      const r = await fetch('/api/admin/finance-audit?' + params.toString(), { headers: authHeaders });
      if (!r.ok) { addToast('CSV export failed: ' + r.statusText, 'error'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `finance-audit-${financeFrom}_${financeTo}.csv`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      addToast('✅ CSV downloaded', 'success');
    } catch (e) { addToast('❌ CSV export error: ' + e.message, 'error'); }
  };

  // Clicking a recordId loads that record's change-history (same table, same range).
  const openFinanceHistory = (ev) => {
    const id = String(ev.recordId);
    setFinanceFilter(prev => ({ ...prev, recordId: id, table: ev.table || prev.table }));
    setFinanceOffset(0);
    loadFinance({ filterOverride: { recordId: id, table: ev.table || financeFilter.table }, offsetOverride: 0 });
  };
  const clearFinanceHistory = () => {
    setFinanceFilter(prev => ({ ...prev, recordId: '' }));
    setFinanceOffset(0);
    loadFinance({ filterOverride: { recordId: '' }, offsetOverride: 0 });
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const r = await fetch('/api/admin/sessions', { headers: authHeaders });
      const d = await r.json();
      if (d.ok) setSessions(d.sessions || []);
    } catch (e) { addToast('Failed to load sessions: ' + e.message, 'error'); }
    finally { setSessionsLoading(false); }
  };

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const r = await fetch('/api/admin/devices', { headers: authHeaders });
      const d = await r.json();
      if (d.ok) setDevices(d.devices || []);
    } catch (e) { addToast('Failed to load devices: ' + e.message, 'error'); }
    finally { setDevicesLoading(false); }
  };

  // Без подтверждения сознательно: цена ошибочного нажатия — человек один раз введёт
  // код из Telegram при следующем входе. Для kill session цена выше, поэтому там confirm есть.
  const revokeDevice = async (id, email) => {
    try {
      const r = await fetch('/api/admin/devices/' + encodeURIComponent(id), { method: 'DELETE', headers: authHeaders });
      const d = await r.json();
      if (d.ok) { addToast('✅ Device revoked: ' + (d.email || email), 'success'); loadDevices(); }
      else addToast('❌ ' + (d.error || 'Failed'), 'error');
    } catch (e) { addToast('❌ Error: ' + e.message, 'error'); }
  };

  /** Сколько доверенных устройств у этого человека — показывается прямо в строке сессии. */
  const devicesFor = (email) => devices.filter(d => d.email === String(email || '').toLowerCase()).length;

  // Один клик на оба действия: завершить сессию И снять метки. Нужен для разбора
  // инцидента «устройство увели», где по отдельности легко сделать половину работы.
  const killSessionAndDevices = async (token, email) => {
    if (!(await askConfirm({
      title: 'Завершить сессию и снять доверенные устройства?',
      message: `${email}\n\nЧеловек будет разлогинен, а при следующем входе снова понадобится код из Telegram.`,
      confirmLabel: 'Завершить и снять',
      danger: true,
    }))) return;
    try {
      const r1 = await fetch('/api/admin/sessions/' + encodeURIComponent(token), { method: 'DELETE', headers: authHeaders });
      const d1 = await r1.json();
      const r2 = await fetch('/api/admin/devices?email=' + encodeURIComponent(email), { method: 'DELETE', headers: authHeaders });
      const d2 = await r2.json();
      // Отчитываемся по каждому действию отдельно: снятие меток может не лечь на диск,
      // и в этом случае админ обязан узнать, а не увидеть общее «готово».
      if (d1.ok) addToast('✅ Сессия завершена: ' + email, 'success');
      else addToast('❌ Сессия: ' + (d1.error || 'не удалось'), 'error');
      if (d2.ok) addToast(`✅ Снято доверенных устройств: ${d2.revoked}`, 'success');
      else addToast('❌ Устройства: ' + (d2.error || 'не удалось'), 'error');
      loadSessions(); loadDevices();
    } catch (e) { addToast('❌ Ошибка: ' + e.message, 'error'); }
  };

  const loadIPs = async () => {
    setIpsLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch('/api/admin/banned', { headers: authHeaders }).then(r => r.json()),
        fetch('/api/admin/login-attempts', { headers: authHeaders }).then(r => r.json()),
      ]);
      if (r1.ok) setBlacklist(r1.banned || []);
      if (r2.ok) setAttempts(r2.attempts || []);
    } catch (e) { addToast('Failed to load IP data: ' + e.message, 'error'); }
    finally { setIpsLoading(false); }
  };

  const killSession = async (token) => {
    if (!(await askConfirm({
      title: 'Kill this session?',
      message: 'User will be forcibly logged out.',
      confirmLabel: 'Kill session',
      cancelLabel: 'Cancel',
      danger: true,
    }))) return;
    try {
      const r = await fetch('/api/admin/sessions/' + encodeURIComponent(token), { method: 'DELETE', headers: authHeaders });
      const d = await r.json();
      if (d.ok) { addToast('✅ Session killed: ' + d.email, 'success'); loadSessions(); }
      else addToast('❌ ' + (d.error || 'Failed'), 'error');
    } catch (e) { addToast('❌ Error: ' + e.message, 'error'); }
  };

  const unbanIP = async (ip) => {
    if (!(await askConfirm({
      title: 'Unblock IP ' + ip + '?',
      message: 'User from this IP will be able to log in again.',
      confirmLabel: 'Unblock',
      cancelLabel: 'Cancel',
    }))) return;
    try {
      const r = await fetch('/api/admin/unban', { method: 'POST', headers: authHeaders, body: JSON.stringify({ ip }) });
      const d = await r.json();
      if (d.ok) { addToast('✅ IP unbanned: ' + ip, 'success'); loadIPs(); }
      else addToast('❌ ' + (d.error || 'Failed'), 'error');
    } catch (e) { addToast('❌ Error: ' + e.message, 'error'); }
  };

  const banIPManual = async () => {
    if (!banForm.ip) { addToast('Please enter IP address', 'error'); return; }
    try {
      const r = await fetch('/api/admin/ban', { method: 'POST', headers: authHeaders, body: JSON.stringify(banForm) });
      const d = await r.json();
      if (d.ok) { addToast('✅ IP banned: ' + banForm.ip, 'success'); setBanForm({ ip: '', reason: '' }); loadIPs(); }
      else addToast('❌ ' + (d.error || 'Failed'), 'error');
    } catch (e) { addToast('❌ Error: ' + e.message, 'error'); }
  };

  const resetAttempts = async (ip) => {
    try {
      const r = await fetch('/api/admin/login-attempts/' + encodeURIComponent(ip), { method: 'DELETE', headers: authHeaders });
      const d = await r.json();
      if (d.ok) { addToast('✅ Counter reset: ' + ip, 'success'); loadIPs(); }
    } catch (e) { addToast('❌ Error: ' + e.message, 'error'); }
  };

  // 2026-05-16 ESLint burndown: admin tab data loaders. loadX functions are inline
  // (recreated each render). Adding к deps would fire effect every render = wasteful
  // (current behavior: load when relevant state changes, NOT every render). Suppression
  // matches design intent. Same pattern PR#84/#85/#88/#90 (intentional run-once effects).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAuditDates(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, auditDate, auditOffset]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'sessions') { loadSessions(); loadDevices(); } }, [tab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'ips') loadIPs(); }, [tab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'finance') loadFinance(); }, [tab, financeFrom, financeTo, financeOffset]);
  // Reset offset when date or filters change
  useEffect(() => { setAuditOffset(0); }, [auditDate]);
  useEffect(() => { setFinanceOffset(0); }, [financeFrom, financeTo]);

  const tabBtn = (key, label) => ({
    padding: "10px 20px", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600,
    background: tab === key ? "linear-gradient(135deg,#8B5CF6,#A78BFA)" : "transparent",
    color: tab === key ? "#FFF" : "#64748B",
    borderRadius: 8, transition: "all 0.2s",
  });
  const td = { padding: "10px 12px", borderBottom: "1px solid #E2E8F0", fontSize: 13, color: "#0F172A" };
  const th = { padding: "10px 12px", background: "#F8FAFC", fontSize: 11, fontWeight: 700, color: "#64748B", textAlign: "left", textTransform: "uppercase", letterSpacing: 0.5 };

  return (
    <div style={{ minHeight: "100vh", background: "#FFFFFF", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", color: "#0F172A" }}>
      <header className="blitz-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #E2E8F0", background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 100, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {I.logo}
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 16 }}>Blitz CRM</span>
          <span style={{ fontSize: 10, color: "#64748B", fontFamily: "'JetBrains Mono',monospace", background: "#E2E8F0", padding: "2px 7px", borderRadius: 6 }}>v{VERSION}</span>
          <span style={{ color: "#64748B", fontSize: 13 }}>/ 🛡️ Security Dashboard</span>
        </div>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #E2E8F0", color: "#64748B", cursor: "pointer", fontSize: 12, fontWeight: 500, padding: "7px 12px", borderRadius: 8 }}>
          <ChevronLeft size={14} strokeWidth={2.5} /><span>Back to Admin</span>
        </button>
      </header>

      <main style={{ maxWidth: 1760, margin: "0 auto", padding: "20px 16px" }}>
        <h1 style={{ margin: "0 0 20px 0", fontSize: 24, fontWeight: 800 }}>Security Dashboard</h1>

        {/* Tab selector */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1px solid #E2E8F0", paddingBottom: 12 }}>
          <button onClick={() => setTab('audit')} style={tabBtn('audit')}>📋 Audit Log</button>
          <button onClick={() => setTab('sessions')} style={tabBtn('sessions')}>👥 Sessions</button>
          <button onClick={() => setTab('ips')} style={tabBtn('ips')}>🚫 IP Management</button>
          <button onClick={() => setTab('finance')} style={tabBtn('finance')}>💰 Finance</button>
        </div>

        {/* AUDIT TAB */}
        {tab === 'audit' && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Date:</label>
              <select value={auditDate} onChange={e => setAuditDate(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}>
                {auditDates.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input placeholder="Filter by action type..." value={auditFilter.type} onChange={e => setAuditFilter({ ...auditFilter, type: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 150 }} />
              <input placeholder="User email..." value={auditFilter.user} onChange={e => setAuditFilter({ ...auditFilter, user: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 150 }} />
              <input placeholder="IP..." value={auditFilter.ip} onChange={e => setAuditFilter({ ...auditFilter, ip: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 120 }} />
              <button onClick={() => { if (auditOffset !== 0) setAuditOffset(0); else loadAudit(); }} style={{ padding: "6px 14px", background: "#0EA5E9", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Apply</button>
              <button onClick={exportAuditCSV} title="Download all filtered events for this date as CSV" style={{ padding: "6px 14px", background: "linear-gradient(135deg,#10B981,#34D399)", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>📥 Export CSV</button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 12, color: "#64748B" }}>
              <div>
                Showing {auditEvents.length > 0 ? `${auditOffset + 1}–${auditOffset + auditEvents.length}` : '0'} of {auditTotal} events
                {auditLoading && ' (loading...)'}
              </div>
              {auditTotal > AUDIT_PAGE_SIZE && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button onClick={() => setAuditOffset(Math.max(0, auditOffset - AUDIT_PAGE_SIZE))} disabled={auditOffset === 0} style={{ padding: "4px 10px", background: auditOffset === 0 ? "#E2E8F0" : "#FFF", border: "1px solid #E2E8F0", borderRadius: 6, cursor: auditOffset === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: auditOffset === 0 ? "#94A3B8" : "#0F172A" }}>← Prev</button>
                  <span style={{ padding: "0 8px", fontWeight: 700, color: "#0F172A" }}>Page {Math.floor(auditOffset / AUDIT_PAGE_SIZE) + 1} / {Math.max(1, Math.ceil(auditTotal / AUDIT_PAGE_SIZE))}</span>
                  <button onClick={() => setAuditOffset(auditOffset + AUDIT_PAGE_SIZE)} disabled={auditOffset + AUDIT_PAGE_SIZE >= auditTotal} style={{ padding: "4px 10px", background: (auditOffset + AUDIT_PAGE_SIZE >= auditTotal) ? "#E2E8F0" : "#FFF", border: "1px solid #E2E8F0", borderRadius: 6, cursor: (auditOffset + AUDIT_PAGE_SIZE >= auditTotal) ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: (auditOffset + AUDIT_PAGE_SIZE >= auditTotal) ? "#94A3B8" : "#0F172A" }}>Next →</button>
                </div>
              )}
            </div>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto", maxHeight: 600 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr><th style={th}>Time</th><th style={th}>Action</th><th style={th}>Table</th><th style={th}>User</th><th style={th}>IP</th><th style={th}>Details</th></tr>
                </thead>
                <tbody>
                  {auditEvents.map((e, i) => (
                    <tr key={i}>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, whiteSpace: "nowrap" }}>{(e.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
                      <td style={{ ...td, fontWeight: 600, color: (e.action || '').includes('fail') || (e.action || '').includes('ban') ? "#EF4444" : (e.action || '').includes('success') ? "#10B981" : "#0F172A" }}>{e.action}</td>
                      <td style={td}>{e.table || '-'}</td>
                      <td style={{ ...td, fontSize: 12 }}>{e.user || '-'}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{e.ip || '-'}</td>
                      <td style={{ ...td, fontSize: 11, color: "#64748B", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>{e.details || ''}</td>
                    </tr>
                  ))}
                  {auditEvents.length === 0 && !auditLoading && (
                    <tr><td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>No events match filters for {auditDate}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SESSIONS TAB */}
        {tab === 'sessions' && (() => {
          const q = sessionsSearch.toLowerCase().trim();
          const filteredSessions = q
            ? sessions.filter(s =>
                (s.email || '').toLowerCase().includes(q) ||
                (s.name || '').toLowerCase().includes(q) ||
                (s.pageAccess || []).join(',').toLowerCase().includes(q)
              )
            : sessions;
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <input
                  placeholder="🔍 Search by email, name, or access..."
                  value={sessionsSearch}
                  onChange={e => setSessionsSearch(e.target.value)}
                  style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: "1 1 250px", maxWidth: 400 }}
                />
                <div style={{ fontSize: 13, color: "#64748B" }}>
                  {q ? `${filteredSessions.length} of ${sessions.length} sessions` : `Active sessions: ${sessions.length}`}
                  {sessionsLoading && ' (loading...)'}
                </div>
                <button onClick={loadSessions} style={{ padding: "6px 14px", background: "#0EA5E9", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>🔄 Refresh</button>
              </div>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr><th style={th}>Token</th><th style={th}>User</th><th style={th}>Name</th><th style={th}>Access</th><th style={th}>Created</th><th style={th}>Action</th></tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map(s => (
                      <tr key={s.token}>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{s.tokenPrefix}</td>
                        <td style={td}>{s.email}</td>
                        <td style={td}>{s.name || '-'}</td>
                        <td style={{ ...td, fontSize: 11, color: "#64748B" }}>{(s.pageAccess || []).slice(0, 3).join(', ')}{(s.pageAccess || []).length > 3 ? '…' : ''}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{s.createdAt ? tz.fmtDateTime(new Date(s.createdAt)) : '-'}</td>
                        <td style={td}>
                          {/* 2026-08-07 — счётчик доверенных устройств прямо в строке сессии.
                              Ревью нашло разрыв: админ, разбирающий «ноутбук украли», жмёт
                              привычный Kill session и считает инцидент закрытым, а обход 2FA
                              остаётся живым ещё до 30 дней. Убить сессию и снять метки — два
                              разных действия, и об этом должно быть видно в момент решения. */}
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <button onClick={() => killSession(s.token)} style={{ padding: "4px 10px", background: "#EF4444", color: "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Kill</button>
                            {devicesFor(s.email) > 0 && (
                              <button
                                onClick={() => killSessionAndDevices(s.token, s.email)}
                                title={`У этого человека ${devicesFor(s.email)} доверенн. устройств(а) — они пропускают 2FA до 30 дней. Kill session их НЕ снимает.`}
                                style={{ padding: "4px 10px", background: "#B45309", color: "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                              >Kill + {devicesFor(s.email)} 🖥️</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredSessions.length === 0 && !sessionsLoading && (
                      <tr><td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>{q ? `No sessions match "${sessionsSearch}"` : 'No active sessions'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* 2026-08-07 — доверенные устройства. Отличие от сессий выше: сессия живёт
                  сутки и даёт доступ прямо сейчас, а метка живёт 30 дней и лишь пропускает
                  второй фактор при следующем входе (пароль всё равно спрашивается).
                  Device — подпись из User-Agent, она для узнавания глазами, не проверка. */}
              <div style={{ marginTop: 28 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                    🖥️ Trusted devices
                    <span style={{ fontWeight: 400, fontSize: 12, color: "#64748B", marginLeft: 8 }}>
                      skip 2FA for 30 days · password still required
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B" }}>
                    {devices.length} device{devices.length === 1 ? '' : 's'}{devicesLoading && ' (loading...)'}
                  </div>
                </div>
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr><th style={th}>User</th><th style={th}>Device</th><th style={th}>Last used</th><th style={th}>Last IP</th><th style={th}>Expires</th><th style={th}>Action</th></tr>
                    </thead>
                    <tbody>
                      {devices.map(d => {
                        // Number.isFinite, а не просто вычитание: запись без expiresAt дала бы
                        // NaN и в таблице появилось бы «in NaNd». Сейчас такая запись до API
                        // не доходит (фильтр при загрузке реестра), но показывать мусор вместо
                        // срока в панели безопасности нельзя даже теоретически.
                        const daysLeft = Number.isFinite(d.expiresAt)
                          ? Math.max(0, Math.ceil((d.expiresAt - Date.now()) / 86400000))
                          : null;
                        return (
                          <tr key={d.id}>
                            <td style={td}>{d.email}</td>
                            <td style={td}>{d.label || '-'}</td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{d.lastUsedAt ? tz.fmtDateTime(new Date(d.lastUsedAt)) : '-'}</td>
                            <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{d.lastIp || '-'}</td>
                            <td style={{ ...td, fontSize: 12, color: daysLeft !== null && daysLeft <= 3 ? "#B45309" : "#64748B" }}>{daysLeft === null ? '—' : `in ${daysLeft}d`}</td>
                            <td style={td}>
                              <button onClick={() => revokeDevice(d.id, d.email)} style={{ padding: "4px 10px", background: "#EF4444", color: "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Revoke</button>
                            </td>
                          </tr>
                        );
                      })}
                      {devices.length === 0 && !devicesLoading && (
                        <tr><td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>No trusted devices yet — they appear after a successful 2FA login</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* IPs TAB */}
        {tab === 'ips' && (
          <div>
            {/* Blacklist section */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🚫 Permanent Blacklist ({blacklist.length})</h2>
                <button onClick={loadIPs} style={{ padding: "6px 14px", background: "#0EA5E9", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>🔄 Refresh</button>
              </div>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr><th style={th}>IP Address</th><th style={th}>Action</th></tr>
                  </thead>
                  <tbody>
                    {blacklist.map(ip => (
                      <tr key={ip}>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace" }}>{ip}</td>
                        <td style={td}>
                          <button onClick={() => unbanIP(ip)} style={{ padding: "4px 10px", background: "#10B981", color: "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Unblock</button>
                        </td>
                      </tr>
                    ))}
                    {blacklist.length === 0 && <tr><td colSpan={2} style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>No banned IPs</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Manual ban form */}
            <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 12, padding: 16, marginBottom: 28 }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 700 }}>⚠️ Manual Ban IP</h3>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input placeholder="IP address (e.g. 1.2.3.4)" value={banForm.ip} onChange={e => setBanForm({ ...banForm, ip: e.target.value })} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: "1 1 200px" }} />
                <input placeholder="Reason (optional)" value={banForm.reason} onChange={e => setBanForm({ ...banForm, reason: e.target.value })} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: "2 1 300px" }} />
                <button onClick={banIPManual} style={{ padding: "8px 20px", background: "#EF4444", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Ban IP</button>
              </div>
              <div style={{ fontSize: 11, color: "#92400E", marginTop: 8 }}>Localhost, private networks (10.x, 192.168.x, 172.16-31.x), and whitelisted IPs cannot be banned.</div>
            </div>

            {/* Login attempts counters */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>⏱️ Active Login Attempts (in-memory, 10 min TTL)</h2>
              </div>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr><th style={th}>IP</th><th style={th}>Count</th><th style={th}>Remaining</th><th style={th}>First At</th><th style={th}>Action</th></tr>
                  </thead>
                  <tbody>
                    {attempts.map(a => (
                      <tr key={a.ip}>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace" }}>{a.ip}</td>
                        <td style={{ ...td, fontWeight: 700, color: a.count >= 3 ? "#EF4444" : a.count >= 2 ? "#F59E0B" : "#0F172A" }}>{a.count}</td>
                        <td style={td}>{a.remaining}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{a.firstAt ? tz.fmtDateTime(new Date(a.firstAt)) : '-'}</td>
                        <td style={td}>
                          <button onClick={() => resetAttempts(a.ip)} style={{ padding: "4px 10px", background: "#94A3B8", color: "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Reset</button>
                        </td>
                      </tr>
                    ))}
                    {attempts.length === 0 && <tr><td colSpan={5} style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>No active attempts</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* FINANCE TAB — read-only viewer over GET /api/admin/finance-audit (PR6) */}
        {tab === 'finance' && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>From:</label>
              <input type="date" value={financeFrom} onChange={e => setFinanceFrom(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <label style={{ fontSize: 13, fontWeight: 600 }}>To:</label>
              <input type="date" value={financeTo} onChange={e => setFinanceTo(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <select value={financeFilter.table} onChange={e => setFinanceFilter({ ...financeFilter, table: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}>
                <option value="">All tables</option>
                {FINANCE_TABLES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={financeFilter.action} onChange={e => setFinanceFilter({ ...financeFilter, action: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}>
                <option value="">All actions</option>
                <option value="update">update</option>
                <option value="delete">delete</option>
                <option value="bulk">bulk</option>
                <option value="create">create</option>
              </select>
              <input placeholder="User..." value={financeFilter.user} onChange={e => setFinanceFilter({ ...financeFilter, user: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 120 }} />
              <input placeholder="Record ID..." value={financeFilter.recordId} onChange={e => setFinanceFilter({ ...financeFilter, recordId: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 120 }} />
              <input placeholder="Invoice..." value={financeFilter.invoice} onChange={e => setFinanceFilter({ ...financeFilter, invoice: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 120 }} />
              <input placeholder="Source..." value={financeFilter.source} onChange={e => setFinanceFilter({ ...financeFilter, source: e.target.value })} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, flex: 1, minWidth: 120 }} />
              <button onClick={() => { if (financeOffset !== 0) setFinanceOffset(0); else loadFinance(); }} style={{ padding: "6px 14px", background: "#0EA5E9", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Apply</button>
              <button onClick={exportFinanceCSV} title="Download all filtered finance-audit rows as CSV" style={{ padding: "6px 14px", background: "linear-gradient(135deg,#10B981,#34D399)", color: "#FFF", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>📥 Export CSV</button>
            </div>
            {financeError && (
              <div style={{ marginBottom: 12, padding: "10px 14px", border: "1px solid #EF4444", borderRadius: 8, color: "#EF4444", fontSize: 13, fontWeight: 600 }}>⚠️ {financeError}</div>
            )}
            {financeFilter.recordId && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: "8px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#0F172A" }}>
                <span>📜 История записи #{financeFilter.recordId}{financeFilter.table ? ' · ' + financeFilter.table : ''}</span>
                <button onClick={clearFinanceHistory} style={{ padding: "4px 10px", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#64748B" }}>× Сбросить</button>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 12, color: "#64748B" }}>
              <div>
                Showing {financeEvents.length > 0 ? `${financeOffset + 1}–${financeOffset + financeEvents.length}` : '0'} of {financeTotal} events
                {financeLoading && ' (loading...)'}
              </div>
              {financeTotal > FINANCE_PAGE_SIZE && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button onClick={() => setFinanceOffset(Math.max(0, financeOffset - FINANCE_PAGE_SIZE))} disabled={financeOffset === 0} style={{ padding: "4px 10px", background: financeOffset === 0 ? "#E2E8F0" : "#FFF", border: "1px solid #E2E8F0", borderRadius: 6, cursor: financeOffset === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: financeOffset === 0 ? "#94A3B8" : "#0F172A" }}>← Prev</button>
                  <span style={{ padding: "0 8px", fontWeight: 700, color: "#0F172A" }}>Page {Math.floor(financeOffset / FINANCE_PAGE_SIZE) + 1} / {Math.max(1, Math.ceil(financeTotal / FINANCE_PAGE_SIZE))}</span>
                  <button onClick={() => setFinanceOffset(financeOffset + FINANCE_PAGE_SIZE)} disabled={financeOffset + FINANCE_PAGE_SIZE >= financeTotal} style={{ padding: "4px 10px", background: (financeOffset + FINANCE_PAGE_SIZE >= financeTotal) ? "#E2E8F0" : "#FFF", border: "1px solid #E2E8F0", borderRadius: 6, cursor: (financeOffset + FINANCE_PAGE_SIZE >= financeTotal) ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: (financeOffset + FINANCE_PAGE_SIZE >= financeTotal) ? "#94A3B8" : "#0F172A" }}>Next →</button>
                </div>
              )}
            </div>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto", maxHeight: 600 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr><th style={th}>Time</th><th style={th}>Table</th><th style={th}>Action</th><th style={th}>User</th><th style={th}>Record</th><th style={th}>Source</th><th style={th}>Diff</th><th style={th}>Details</th></tr>
                </thead>
                <tbody>
                  {financeEvents.map((e, i) => {
                    const dd = describeFinanceDiff(e.diff);
                    const actionColor = e.action === 'delete' ? "#EF4444" : e.action === 'create' ? "#10B981" : e.action === 'bulk' ? "#0EA5E9" : "#0F172A";
                    return (
                      <tr key={i}>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, whiteSpace: "nowrap" }}>{(e.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
                        <td style={{ ...td, fontSize: 12 }}>{e.table || '-'}</td>
                        <td style={{ ...td, fontWeight: 600, color: actionColor }}>{e.action || '-'}</td>
                        <td style={{ ...td, fontSize: 12 }}>{e.user || '-'}</td>
                        <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>
                          {e.recordId != null
                            ? <button onClick={() => openFinanceHistory(e)} title="Show change-history for this record" style={{ background: "none", border: "none", padding: 0, color: "#0EA5E9", cursor: "pointer", textDecoration: "underline", fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{String(e.recordId)}</button>
                            : '-'}
                        </td>
                        <td style={{ ...td, fontSize: 12 }}>{e.source || '-'}</td>
                        <td style={{ ...td, fontSize: 11 }}>
                          {dd.kind === 'field' && dd.fields.map((fld, j) => (
                            <div key={j} style={{ whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 600 }}>{fld.key}:</span>{' '}
                              <span style={{ color: "#64748B" }}>{fmtFinanceDiffVal(fld.from)}</span>
                              {' → '}
                              <span style={{ color: "#10B981", fontWeight: 600 }}>{fmtFinanceDiffVal(fld.to)}</span>
                            </div>
                          ))}
                          {dd.kind === 'set' && (
                            <details>
                              <summary style={{ cursor: "pointer" }}>modified: {dd.counts.modified}, removed: {dd.counts.removed}, added: {dd.counts.added}</summary>
                              <div style={{ color: "#64748B", marginTop: 4 }}>
                                {dd.ids.modified.length > 0 && <div>modified: {dd.ids.modified.join(', ')}</div>}
                                {dd.ids.removed.length > 0 && <div>removed: {dd.ids.removed.join(', ')}</div>}
                                {dd.ids.added.length > 0 && <div>added: {dd.ids.added.join(', ')}</div>}
                              </div>
                            </details>
                          )}
                          {dd.kind === 'none' && <span style={{ color: "#94A3B8" }}>—</span>}
                        </td>
                        <td style={{ ...td, fontSize: 11, color: "#64748B", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.details || ''}</td>
                      </tr>
                    );
                  })}
                  {financeEvents.length === 0 && !financeLoading && (
                    <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>{financeFilter.recordId ? `Нет finance-событий для записи #${financeFilter.recordId} за период` : 'Нет finance-событий за период'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {confirmState && (
        <ConfirmDialog
          {...confirmState.opts}
          onConfirm={() => closeConfirm(true)}
          onCancel={() => closeConfirm(false)}
        />
      )}
    </div>
  );
}

export { SecurityPanel };
export default SecurityPanel;
